import { inside, type MotionSpec, type Rect, type RescueLevel, type RescueRole, type Vec } from './types';

export const STROKE_RADIUS = 2.5;
export function distanceToSegment(p: Vec, a: Vec, b: Vec) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(p.x - a.x - dx * t, p.y - a.y - dy * t);
}
/** Liang–Barsky: tests the whole segment, including fast swipes with both ends outside. */
export function intersectsRect(a: Vec, b: Vec, r: Rect, margin = 0) {
  let start = 0, end = 1;
  const dx = b.x - a.x, dy = b.y - a.y;
  for (const [p, q] of [[-dx, a.x - r.x + margin], [dx, r.x + r.w + margin - a.x], [-dy, a.y - r.y + margin], [dy, r.y + r.h + margin - a.y]]) {
    if (Math.abs(p) < 1e-10) { if (q < 0) return false; continue; }
    const t = q / p;
    if (p < 0) start = Math.max(start, t); else end = Math.min(end, t);
    if (start > end) return false;
  }
  return true;
}
function cross(a: Vec, b: Vec, c: Vec) { return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x); }
export function segmentDistance(a: Vec, b: Vec, c: Vec, d: Vec) {
  if (Math.max(a.x, b.x) >= Math.min(c.x, d.x) && Math.max(c.x, d.x) >= Math.min(a.x, b.x) && Math.max(a.y, b.y) >= Math.min(c.y, d.y) && Math.max(c.y, d.y) >= Math.min(a.y, b.y) && cross(a,b,c) * cross(a,b,d) <= 0 && cross(c,d,a) * cross(c,d,b) <= 0) return 0;
  return Math.min(distanceToSegment(a,c,d), distanceToSegment(b,c,d), distanceToSegment(c,a,b), distanceToSegment(d,a,b));
}
export function motionRect(spec: MotionSpec, tick: number): Rect {
  const phase = ((tick + (spec.phase ?? 0)) % spec.period + spec.period) % spec.period / spec.period;
  const t = phase < 0.5 ? phase * 2 : 2 - phase * 2;
  return { x: spec.x + spec.travel.x * t, y: spec.y + spec.travel.y * t, w: spec.w, h: spec.h };
}
export const pulseActive = (spec: { period?: number; on?: number; phase?: number }, tick: number) => !spec.period || (tick + (spec.phase ?? 0)) % spec.period < (spec.on ?? spec.period);
/** Version 1 recordings retain their original drawing geometry; new maps share these checks on all platforms. */
export function blockedSegment(level: RescueLevel, a: Vec, b: Vec, tick: number): string | null {
  if (level.version < 2) return null;
  if (level.goals.some(r => intersectsRect(a, b, r, STROKE_RADIUS + 1))) return '终点内不能画线，请把路接到入口外。';
  if (level.rails.some(r => r.points.slice(1).some((p, i) => segmentDistance(a,b,r.points[i],p) <= STROKE_RADIUS * 2 + 1))) return '线条不能穿过墙面，请绕开墙体。';
  if (level.gates.some(r => intersectsRect(a,b,r,STROKE_RADIUS + 1))) return '请留出闸门通道，不要把线画进门体。';
  if (level.levers.some(l => distanceToSegment(l.center,a,b) < l.width / 2 + 6)) return '请留出跷板转动空间。';
  if (level.motions?.some(m => intersectsRect(a,b,motionRect(m,tick),STROKE_RADIUS + 1))) return '这里正被移动机关占用，等它经过或绕开。';
  return null;
}
/** `from` lets the live preview validate only the newest segments; the full stroke is re-checked on commit. */
export function strokeProblem(level: RescueLevel, points: Vec[], role: RescueRole, tick: number, from = 0): string | null {
  for (let i = Math.max(0, from); i < points.length; i++) {
    const a = points[Math.max(0, i - 1)], b = points[i];
    if (!level.areas[role].some(r => inside(a,r) && inside(b,r))) return '请在自己负责的色框内画线。';
    const blocked = blockedSegment(level,a,b,tick); if (blocked) return blocked;
  }
  return null;
}
